import * as XLSX from "xlsx";
import {
  normalizeWelcomeDrawerReviewDoc,
  welcomeDrawerReviewPivotEntries,
} from "pages/Welcome/welcomeDrawerReviewDoc";
import { resolveDrawerTopicTags } from "pages/Welcome/welcomeDrawerReviewMeta";
import { formatAppDateTime } from "util/formatAppDate";

const SENTIMENT_EXPORT = {
  "1.0": "positive",
  "1": "positive",
  "0.5": "neutral",
  "0.0": "negative",
  "0": "negative",
  "-1": "unspecified",
  "33": "mixed",
};

const INTENT_EXPORT = {
  "-1": "Unspecified",
  "1": "Appraisal",
  "2": "Complaint",
  "3": "Suggestion",
  "4": "Remark",
  "5": "Marketing & Spam",
  "6": "Information",
  "7": "Request",
  "8": "Comparison",
  "33": "mixed",
};

const REVIEW_FIXED_HEADERS = [
  "Gpid",
  "DocDate",
  "DocText",
  "Title/Question",
  "Rating",
  "sentiment",
  "intent",
  "platform",
  "AI Topics",
  "Custom Topics",
  "Internal Id",
];

const HIGHLIGHT_FIXED_HEADERS = [
  "Gpid",
  "DocDate",
  "DocText",
  "Topic",
  "sentiment",
  "intent",
];

function escapeCsvCell(value) {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function normalizeExportKey(value) {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (raw === "33" || raw === "-1") return raw;
  const num = Number(raw);
  if (!Number.isFinite(num)) return raw;
  if (raw.includes(".")) return Number.isInteger(num) ? num.toFixed(1) : raw;
  return String(parseInt(num, 10));
}

function exportSentimentLabel(value) {
  const key = normalizeExportKey(value);
  return SENTIMENT_EXPORT[key] || SENTIMENT_EXPORT[String(Number(key))] || "";
}

function exportIntentLabel(value) {
  const key = normalizeExportKey(value);
  return INTENT_EXPORT[key] || "";
}

function joinComma(items) {
  return items.filter(Boolean).join(",");
}

function collectPivotKeys(rows) {
  const keys = [];
  const seen = new Set();
  rows.forEach((row) => {
    welcomeDrawerReviewPivotEntries(row.pivot).forEach((entry) => {
      if (!entry.key || seen.has(entry.key)) return;
      seen.add(entry.key);
      keys.push(entry.key);
    });
  });
  return keys;
}

function pivotValueMap(row) {
  const map = {};
  welcomeDrawerReviewPivotEntries(row.pivot).forEach((entry) => {
    map[entry.key] = entry.value;
  });
  return map;
}

function splitTopicLabels(row) {
  const tags = resolveDrawerTopicTags(row);
  return {
    ai: joinComma(
      tags.filter((tag) => tag.type === "ai").map((tag) => tag.label),
    ),
    custom: joinComma(
      tags.filter((tag) => tag.type !== "ai").map((tag) => tag.label),
    ),
  };
}

function reviewSequences(review, highlights) {
  const gpid = review?.date_gpid;
  const fromHighlights = gpid ? highlights?.[gpid]?.sequences : null;
  if (Array.isArray(fromHighlights) && fromHighlights.length) {
    return fromHighlights;
  }
  if (Array.isArray(review?.sequences) && review.sequences.length) {
    return review.sequences;
  }
  return [];
}

export function slugWelcomeDrawerExportLabel(label) {
  const slug = String(label ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return slug || "reviews";
}

export function welcomeDrawerReviewsExportFilename(label, options = {}) {
  const now = options.now instanceof Date ? options.now : new Date();
  const fileType = options.fileType === "xlsx" ? "xlsx" : "csv";
  const kind = options.onlySequences ? "highlights" : "reviews";
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${slugWelcomeDrawerExportLabel(label)}-${kind}-${y}${m}${d}.${fileType}`;
}

function buildReviewRows(docs, language) {
  const pivotKeys = collectPivotKeys(docs);
  const headers = [...REVIEW_FIXED_HEADERS, ...pivotKeys];
  const rows = docs.map((row) => {
    const topics = splitTopicLabels(row);
    const pivots = pivotValueMap(row);
    return [
      row.date_gpid ?? "",
      formatAppDateTime(row.timestamp, language),
      row.text || "",
      row.title || "",
      row.num_rating ?? "",
      exportSentimentLabel(row.drawer_sentiment ?? row.num_sentiment),
      exportIntentLabel(row.drawer_intent ?? row.num_intent),
      row.platform || row.platform_name || "",
      topics.ai,
      topics.custom,
      row.internalId ?? row.internal_id ?? "",
      ...pivotKeys.map((key) => pivots[key] ?? ""),
    ];
  });
  return { headers, rows };
}

function buildHighlightRows(docs, language, highlights) {
  const pivotKeys = collectPivotKeys(docs);
  const headers = [...HIGHLIGHT_FIXED_HEADERS, ...pivotKeys];
  const rows = [];
  docs.forEach((review) => {
    const pivots = pivotValueMap(review);
    const pivotCells = pivotKeys.map((key) => pivots[key] ?? "");
    reviewSequences(review, highlights).forEach((sequence) => {
      const associations = Array.isArray(sequence?.topic_associations)
        ? sequence.topic_associations.filter((item) => item?.label || item?.id)
        : [];
      if (!associations.length) return;
      associations.forEach((topic) => {
        rows.push([
          review.date_gpid ?? "",
          formatAppDateTime(
            sequence.date ?? review.timestamp,
            language,
          ),
          sequence.text || "",
          topic.label || "",
          exportSentimentLabel(topic.sentiment),
          exportIntentLabel(topic.intent),
          ...pivotCells,
        ]);
      });
    });
  });
  return { headers, rows };
}

export function buildWelcomeDrawerReviewsTable(
  reviews,
  { language, onlySequences = false, highlights = {} } = {},
) {
  const docs = (Array.isArray(reviews) ? reviews : [])
    .map((row) => normalizeWelcomeDrawerReviewDoc(row))
    .filter(Boolean);
  return onlySequences
    ? buildHighlightRows(docs, language, highlights)
    : buildReviewRows(docs, language);
}

export function tableToCsv({ headers, rows }) {
  const lines = [
    headers.map(escapeCsvCell).join(","),
    ...rows.map((row) => row.map(escapeCsvCell).join(",")),
  ];
  return `\uFEFF${lines.join("\n")}`;
}

export function buildWelcomeDrawerReviewsCsv(reviews, options = {}) {
  return tableToCsv(buildWelcomeDrawerReviewsTable(reviews, options));
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function downloadWelcomeDrawerReviews(reviews, options = {}) {
  const table = buildWelcomeDrawerReviewsTable(reviews, options);
  const filename = welcomeDrawerReviewsExportFilename(options.label, options);
  if (options.onlySequences && table.rows.length === 0) {
    return { filename, rowCount: 0 };
  }
  if (options.fileType === "xlsx") {
    const worksheet = XLSX.utils.aoa_to_sheet([table.headers, ...table.rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      options.onlySequences ? "Highlights" : "Reviews",
    );
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    triggerBlobDownload(
      new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      filename,
    );
  } else {
    triggerBlobDownload(
      new Blob([tableToCsv(table)], { type: "text/csv;charset=utf-8;" }),
      filename,
    );
  }
  return { filename, rowCount: table.rows.length };
}
